import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IShipmentByStatusItem,
  IShipmentProduct,
  IShipmentProductConfig,
  IShipmentSales,
  SHIPMENT_COLUMN_STATUS_ID,
  TShipmentDestinationPlace
} from 'types/orders';
import { CONFIG_STATUS_NAME } from 'types/product.configuration.types';
import { formatShortDate } from 'utils/formatDatesAndHours';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { nudosBlue } from 'styles/styles.constants';
import {
  CONFIG_STATUS_CLASS_MAP,
  CONFIG_STATUS_ORDER,
  getDestinationDisplayName,
  getWarehouseTemporaryStorageReasonNames
} from '../../trackingOrder.utils';
import './ShipmentItem.scss';

interface IShipmentItemProps {
  shipment: IShipmentByStatusItem;
  groupSize?: number;
  currentShipmentNumber?: number;
  totalShipmentsNumber?: number;
  isPreparationChecklistDrawerOpen?: boolean;
  onClick?: () => void;
  onPreparationClick?: () => void;
}

const hasAdditionalActionConfirmedAt = (confirmedAt: unknown): boolean => {
  if (confirmedAt === null || confirmedAt === undefined) return false;
  if (typeof confirmedAt === 'string') return confirmedAt.trim().length > 0;
  return true;
};

/**
 * Componente que representa un ítem de envío dentro de una orden.
 * Muestra información resumida del destino, número de envío y ETA.
 * Soporta click para abrir el detalle del envío.
 */
const ShipmentItem = ({
  shipment,
  groupSize = 1,
  currentShipmentNumber,
  totalShipmentsNumber,
  isPreparationChecklistDrawerOpen,
  onClick,
  onPreparationClick
}: IShipmentItemProps) => {
  const warehouseTemporaryStorageInfo = shipment.warehouseTemporaryStorage?.[0];
  const { preparationChecklistTotal, preparationChecklistCompleted } = shipment;
  const statusId = shipment.shipmentStatusLogs?.[0]?.shipmentStatusId;
  const destinationPlace = shipment.destinationPlace as TShipmentDestinationPlace;
  const countryName = shipment.destinationCountry?.name;
  const baseDestinationName = getDestinationDisplayName(destinationPlace, shipment.receiverName, countryName);
  const destinationName = groupSize > 1 ? `${baseDestinationName} (${groupSize})` : baseDestinationName;

  const toArray = (value: IShipmentProduct[] | IShipmentProductConfig[] | undefined) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  const getConfigs = (): IShipmentProductConfig[] => {
    return (shipment?.sales ?? [])
      .flatMap((sale: IShipmentSales) => toArray(sale?.products) as IShipmentProduct[])
      .flatMap((product: IShipmentProduct) => toArray(product?.productConfigs) as IShipmentProductConfig[])
      .filter(Boolean);
  };

  const getConfigLabel = (config: IShipmentProductConfig): string => {
    const actions = config?.additionalActions ?? [];
    const actionsMissingConfirmedAt = actions.filter(action => !hasAdditionalActionConfirmedAt(action?.confirmedAt));

    if (actionsMissingConfirmedAt.length === 0) {
      return config?.status ?? '';
    }

    const names = actionsMissingConfirmedAt
      .flatMap(action => {
        const list = action?.configAdditionalAction;
        return (Array.isArray(list) ? list : list ? [list] : []).map(a => a?.name).filter(Boolean);
      })
      .filter(Boolean);

    return names.length ? names.join(', ') : config.status ?? '';
  };

  const getConfigurationTooltipEntries = (): { label: string; count: number }[] => {
    const labelCounts = getConfigs().reduce<Record<string, number>>((acc, config) => {
      const label = getConfigLabel(config);
      if (label) acc[label] = (acc[label] ?? 0) + 1;
      return acc;
    }, {});

    return Object.entries(labelCounts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => {
        const getRank = (label: string) => {
          const order = CONFIG_STATUS_ORDER.indexOf(label as CONFIG_STATUS_NAME);
          return order === -1 ? CONFIG_STATUS_ORDER.length : order;
        };
        const rankDiff = getRank(a.label) - getRank(b.label);
        return rankDiff !== 0 ? rankDiff : a.label.localeCompare(b.label);
      });
  };

  const getConfigurationTooltipLineClassName = (label: string): string => {
    const base = 'shipmentItemConfigurationTooltip__line';
    const modifier = CONFIG_STATUS_CLASS_MAP[label as CONFIG_STATUS_NAME];
    return modifier ? `${base} ${modifier}` : base;
  };

  const hasConfigurations = getConfigs()?.length > 0;
  const configurationTooltipEntries = getConfigurationTooltipEntries();

  const configIconRef = useRef<HTMLDivElement>(null);
  const [isConfigTooltipVisible, setIsConfigTooltipVisible] = useState(false);

  const warehouseIconRef = useRef<HTMLDivElement>(null);
  const [isWarehouseTooltipVisible, setIsWarehouseTooltipVisible] = useState(false);

  const isConfirmed = statusId === SHIPMENT_COLUMN_STATUS_ID.CONFIRMED;
  const isPreparation = statusId === SHIPMENT_COLUMN_STATUS_ID.PREPARATION;
  const isTransitToWarehouse = statusId === SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_WAREHOUSE;
  const isInWarehouse = statusId === SHIPMENT_COLUMN_STATUS_ID.IN_WAREHOUSE;
  const isTransitToDestination = statusId === SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_DESTINATION;
  const isDelivered = statusId === SHIPMENT_COLUMN_STATUS_ID.DELIVERED;

  const shouldRenderShipmentSection = !isConfirmed;
  const shouldRenderDateSection = !isConfirmed && !isPreparation;
  const noDate = 'Sin fecha';

  /**
   * Determina si la fecha estimada de entrega ya pasó.
   * Aplica cuando el envío está en tránsito a bodega o a destino final.
   */
  const isEtaOverdue = (() => {
    const renderEtaDate = isTransitToWarehouse ? warehouseTemporaryStorageInfo?.estimatedEntryDate : shipment.estimatedDeliveryDate;
    if ((!isTransitToDestination && !isTransitToWarehouse) || !renderEtaDate) return false;
    const etaDate = new Date(renderEtaDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return etaDate < today;
  })();

  const warehouseStopText = 'Parada en bodega';
  const warehouseStopReasonNames = getWarehouseTemporaryStorageReasonNames(shipment.warehouseTemporaryStorage);
  const showWarehouseTemporaryStorageIcon = isTransitToWarehouse && Boolean(shipment.warehouseTemporaryStorage?.length);
  const warehouseTemporaryStorageTooltipMessage = warehouseStopReasonNames.length
    ? `${warehouseStopText} por: ${warehouseStopReasonNames.join(', ')}`
    : warehouseStopText;

  /**
   * Obtiene la fecha formateada a mostrar según el estado del envío.
   * Entregado: deliveryDate; Llegó: shipmentStatusDates.inWarehouse; ETA: estimatedDeliveryDate.
   */
  const getDisplayDate = (): string => {
    let dateToFormat: string | null | undefined;
    if (isDelivered) dateToFormat = shipment.deliveryDate;
    else if (isInWarehouse) dateToFormat = shipment.shipmentStatusDates?.inWarehouse;
    else if (isTransitToWarehouse) dateToFormat = warehouseTemporaryStorageInfo?.estimatedEntryDate;
    else dateToFormat = shipment.estimatedDeliveryDate;
    return dateToFormat ? formatShortDate(dateToFormat)?.onlyDate || noDate : noDate;
  };

  /**
   * Obtiene el label de la fecha según el estado del envío.
   */
  const getDateLabel = (): string => {
    if (isDelivered) return 'Entregado';
    if (isInWarehouse) return 'Llegó';
    if (isTransitToWarehouse) return 'ETA Bod';
    return 'ETA';
  };

  const handleClick = () => onClick?.();
  const isClickable = Boolean(onClick);
  const itemClassName = ['shipmentItem', isClickable && 'clickable', isEtaOverdue && 'etaOverdue']
    .filter(Boolean)
    .join(' ');

  /**
   * Maneja el click en el botón de checklist de preparación.
   * Previene la propagación para evitar que se dispare el onClick del contenedor.
   * @param e - Evento del mouse
   */
  const handlePreparationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreparationClick?.();
  };

  return (
    <div className={itemClassName} onClick={handleClick} role={isClickable ? 'button' : undefined}>
      <span className="destinationName">
        <div className="destinationContainer">
          <div className="infoName">
            <span className="destinationText" title={destinationName}>
              {destinationName}
            </span>
            <b className="shipmentsIndex">
              {currentShipmentNumber}/{totalShipmentsNumber}
            </b>
          </div>
          {(showWarehouseTemporaryStorageIcon || hasConfigurations) && (
            <div className="shipmentItemTrailingIcons">
              {showWarehouseTemporaryStorageIcon && (
                <div
                  ref={warehouseIconRef}
                  className="warehouseTemporaryStorageIconWrapper"
                  onMouseEnter={() => setIsWarehouseTooltipVisible(true)}
                  onMouseLeave={() => setIsWarehouseTooltipVisible(false)}
                >
                  <BordOneToneIcon variant="productLoading" standardSize={14} />
                  {isWarehouseTooltipVisible &&
                    warehouseIconRef.current &&
                    createPortal(
                      <div
                        className="bordTooltipMessageContainerLegacy shipmentItemWarehouseTemporaryStorageTooltip"
                        style={{
                          position: 'fixed',
                          display: 'block',
                          top: warehouseIconRef.current.getBoundingClientRect().bottom + 2,
                          right: window.innerWidth - warehouseIconRef.current.getBoundingClientRect().right,
                          left: 'auto',
                          zIndex: 9999
                        }}
                      >
                        <div className="messageText">{warehouseTemporaryStorageTooltipMessage}</div>
                      </div>,
                      document.body
                    )}
                </div>
              )}
              {hasConfigurations && (
                <div
                  ref={configIconRef}
                  className="configurationIconWrapper"
                  onMouseEnter={() => setIsConfigTooltipVisible(true)}
                  onMouseLeave={() => setIsConfigTooltipVisible(false)}
                >
                  <BordOneToneIcon
                    variant="settings"
                    standardSize={16}
                    className="self-center z-50"
                    stroke="currentColor"
                  />
                  {isConfigTooltipVisible &&
                    configIconRef.current &&
                    createPortal(
                      <div
                        className="bordTooltipMessageContainerLegacy shipmentItemConfigurationTooltip"
                        style={{
                          position: 'fixed',
                          display: 'block',
                          top: configIconRef.current.getBoundingClientRect().bottom + 2,
                          left: configIconRef.current.getBoundingClientRect().left - 100,
                          zIndex: 9999
                        }}
                      >
                        <div className="messageText shipmentItemConfigurationTooltip__lines">
                          {configurationTooltipEntries.map(({ label, count }) => (
                            <span key={label} className={getConfigurationTooltipLineClassName(label)}>
                              {label}
                              {count > 1 && <span className="shipmentItemConfigurationTooltip__count">{count}</span>}
                            </span>
                          ))}
                        </div>
                      </div>,
                      document.body
                    )}
                </div>
              )}
            </div>
          )}
        </div>
      </span>
      {shouldRenderShipmentSection && (
        <>
          {isPreparation ? (
            <div className="shipmentNumberWrapper" onClick={handlePreparationClick}>
              <button aria-label="Ver checklist de preparación">
                <BordOneToneIcon
                  variant="rightToLeftListBullet"
                  standardSize={16}
                  customWidth="11px"
                  stroke={isPreparationChecklistDrawerOpen ? nudosBlue : undefined}
                />
              </button>
              <span className={`shipmentNumber ${isPreparationChecklistDrawerOpen && 'preparationDrawerOpen'}`}>
                ({preparationChecklistCompleted}/{preparationChecklistTotal})
              </span>{' '}
            </div>
          ) : (
            <span className="shipmentNumber">Envío {currentShipmentNumber}</span>
          )}
        </>
      )}
      {shouldRenderDateSection && (
        <span className="etaDate">
          <b>{getDateLabel()}:</b> {getDisplayDate()}
        </span>
      )}
    </div>
  );
};

export default ShipmentItem;
